import { useEffect, useRef } from 'react';
import { AlertTriangle, Info, Loader2, ShieldCheck, ShieldAlert } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { formatCurrencyFromCents } from '../../../utils/formatCurrency';
import { PAYMENT_PROCEED_MODAL_Z_INDEX } from '../../../utils/paymentProceedStacking';

export interface PaymentProceedNotice {
  type: 'info' | 'warning' | 'error';
  title: string;
  description: string;
}

export interface PaymentProceedVerifiedName {
  publicName?: string;
  legalName?: string;
}

export interface PaymentProceedPayload {
  /** Canonical amount to debit, in cents. */
  amountCents: number;
  /** Primary recipient label: beneficiary name, merchant, or product/rail label. */
  recipientLabel: string;
  /** Optional masked secondary: phone (••••6710), account (••••1234), meter #, wallet addr. */
  recipientSubLabel?: string;
  /** Neutral rail / product context, e.g. "PayShap real-time payment", "Cash withdrawal PIN". */
  contextLabel?: string;
  /** Optional fee in cents. */
  feeAmountCents?: number;
  /** Optional total debit in cents (amount + fee). */
  totalDebitCents?: number;
  /** PayShap bank-verified account name. */
  verifiedName?: PaymentProceedVerifiedName;
  /** Optional notices shown above the action buttons. */
  notices?: PaymentProceedNotice[];
  /** Override the default "Yes, proceed" button text. */
  proceedButtonText?: string;
  /** Override the default "No" button text. */
  cancelButtonText?: string;
  /** Override the default "Confirm payment" title. */
  title?: string;
}

interface PaymentProceedModalProps {
  open: boolean;
  payload: PaymentProceedPayload | null;
  onProceed: () => void;
  onCancel: () => void;
  /** When true, both buttons disable and Proceed shows a spinner. Tie to the flow's isProcessing. */
  isProcessing?: boolean;
  /** OTT Instant Payment JIT: collect recipient ID before debit. */
  recipientIdentityPrompt?: boolean;
  recipientIdValue?: string;
  onRecipientIdChange?: (value: string) => void;
  recipientIdValid?: boolean;
  recipientIdError?: string | null;
}

const NOTICE_STYLES: Record<
  PaymentProceedNotice['type'],
  { bg: string; border: string; color: string }
> = {
  info: { bg: '#e0f2fe', border: '#2D8CCA', color: '#0c4a6e' },
  warning: { bg: '#fef3c7', border: '#f59e0b', color: '#92400e' },
  error: { bg: '#fee2e2', border: '#dc2626', color: '#991b1b' },
};

const NOTICE_ICON: Record<PaymentProceedNotice['type'], typeof Info> = {
  info: Info,
  warning: AlertTriangle,
  error: AlertTriangle,
};

export function PaymentProceedModal({
  open,
  payload,
  onProceed,
  onCancel,
  isProcessing = false,
  recipientIdentityPrompt = false,
  recipientIdValue = '',
  onRecipientIdChange,
  recipientIdValid = true,
  recipientIdError = null,
}: PaymentProceedModalProps) {
  const cancelBtnRef = useRef<HTMLButtonElement | null>(null);

  // Focus the No (cancel) button when the modal opens — safe default that
  // prevents an accidental Enter/Space from proceeding a debit.
  useEffect(() => {
    if (open && cancelBtnRef.current) {
      const id = window.setTimeout(() => {
        cancelBtnRef.current?.focus();
      }, 50);
      return () => window.clearTimeout(id);
    }
    return undefined;
  }, [open]);

  if (!open || !payload) return null;

  const {
    amountCents,
    recipientLabel,
    recipientSubLabel,
    contextLabel,
    feeAmountCents,
    totalDebitCents,
    verifiedName,
    notices,
    proceedButtonText = 'Yes, proceed',
    cancelButtonText = 'No',
    title = 'Confirm payment',
  } = payload;

  const showFeeRows =
    typeof feeAmountCents === 'number' && typeof totalDebitCents === 'number';
  const showVerifiedBlock = !!(
    verifiedName &&
    (verifiedName.publicName || verifiedName.legalName)
  );

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen && !isProcessing) onCancel(); }}>
      <DialogContent
        className="max-w-sm mx-auto"
        overlayClassName="z-[10050]"
        overlayStyle={{ zIndex: PAYMENT_PROCEED_MODAL_Z_INDEX }}
        style={{ zIndex: PAYMENT_PROCEED_MODAL_Z_INDEX }}
        data-testid="payment-proceed-dialog"
        aria-describedby="payment-proceed-description"
      >
        <DialogHeader>
          <DialogTitle
            style={{ fontFamily: 'Montserrat, sans-serif', fontSize: '20px', fontWeight: 700 }}
          >
            {title}
          </DialogTitle>
          <DialogDescription id="payment-proceed-description" className="sr-only">
            You are about to make a payment. Confirm the amount and recipient before proceeding.
          </DialogDescription>
        </DialogHeader>

        {/* Headline sentence */}
        <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
          <p
            id="payment-proceed-headline"
            style={{
              fontSize: '16px',
              lineHeight: 1.5,
              color: '#1f2937',
              margin: 0,
            }}
          >
            You are about to make a payment of{' '}
            <strong style={{ fontWeight: 700 }}>
              {formatCurrencyFromCents(amountCents)}
            </strong>{' '}
            to <strong style={{ fontWeight: 700 }}>{recipientLabel}</strong>.
          </p>

          {recipientSubLabel && (
            <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px', margin: 0 }}>
              {recipientSubLabel}
            </p>
          )}

          {contextLabel && (
            <p
              style={{
                fontSize: '13px',
                color: '#2D8CCA',
                fontWeight: 600,
                marginTop: '8px',
                margin: 0,
              }}
            >
              {contextLabel}
            </p>
          )}
        </div>

        {/* Fee + total rows */}
        {showFeeRows && (
          <div
            style={{
              fontFamily: 'Montserrat, sans-serif',
              borderTop: '1px solid #e2e8f0',
              borderBottom: '1px solid #e2e8f0',
              padding: '12px 0',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px' }}>
              <span style={{ color: '#6b7280' }}>Payment</span>
              <span style={{ color: '#1f2937', fontWeight: 500 }}>
                {formatCurrencyFromCents(amountCents)}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', marginTop: '6px' }}>
              <span style={{ color: '#6b7280' }}>Fee</span>
              <span style={{ color: '#1f2937', fontWeight: 500 }}>
                {formatCurrencyFromCents(feeAmountCents as number)}
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: '16px',
                marginTop: '8px',
                paddingTop: '8px',
                borderTop: '1px dashed #e2e8f0',
              }}
            >
              <span style={{ color: '#1f2937', fontWeight: 600 }}>Total debit</span>
              <span style={{ color: '#1f2937', fontWeight: 700 }}>
                {formatCurrencyFromCents(totalDebitCents as number)}
              </span>
            </div>
          </div>
        )}

        {/* Bank-verified payee block (PayShap account resolution). */}
        {showVerifiedBlock && (
          <div
            className="rounded-lg border border-[#86BE41]/40 bg-[#86BE41]/10 p-3"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck className="w-4 h-4 text-[#4e8a1f]" />
              <span className="text-sm font-semibold text-[#2D5016]">Bank-verified account</span>
            </div>
            <p className="text-sm text-[#1f2937]">
              {verifiedName?.publicName || verifiedName?.legalName}
            </p>
            {verifiedName?.legalName && verifiedName.legalName !== verifiedName.publicName && (
              <p className="text-xs text-[#6b7280] mt-0.5">
                Registered name: {verifiedName.legalName}
              </p>
            )}
            <p className="text-xs text-[#6b7280] mt-1">
              Please confirm this is the account holder you intend to pay.
            </p>
          </div>
        )}

        {/* Notices */}
        {notices && notices.length > 0 && (
          <div className="space-y-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            {notices.map((notice, idx) => {
              const styles = NOTICE_STYLES[notice.type];
              const Icon = NOTICE_ICON[notice.type];
              return (
                <div
                  key={`proceed-notice-${idx}`}
                  style={{
                    backgroundColor: styles.bg,
                    border: `1px solid ${styles.border}`,
                    color: styles.color,
                    borderRadius: '8px',
                    padding: '10px 12px',
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'flex-start',
                  }}
                >
                  {notice.type === 'warning' ? (
                    <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  ) : (
                    <Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  )}
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 600, margin: 0 }}>{notice.title}</p>
                    <p style={{ fontSize: '11px', opacity: 0.85, margin: '2px 0 0 0' }}>
                      {notice.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {recipientIdentityPrompt && (
          <div style={{ fontFamily: 'Montserrat, sans-serif' }}>
            <label
              htmlFor="recipient-identity-input"
              style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: '#1f2937' }}
            >
              To send money, enter the recipient's SA ID or passport number.
            </label>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 8px 0' }}>
              This Instant Payment needs the ID of the person who owns that bank account.
            </p>
            <input
              id="recipient-identity-input"
              type="text"
              inputMode="text"
              autoComplete="off"
              value={recipientIdValue}
              onChange={(event) => onRecipientIdChange?.(event.target.value)}
              placeholder="13-digit SA ID or passport"
              disabled={isProcessing}
              style={{
                width: '100%',
                minHeight: '44px',
                border: '1px solid #cbd5e1',
                borderRadius: '8px',
                padding: '8px 12px',
                fontSize: '16px',
              }}
            />
            {recipientIdError && (
              <p style={{ fontSize: '12px', color: '#dc2626', margin: '6px 0 0 0' }}>{recipientIdError}</p>
            )}
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-3 pt-2">
          <Button
            ref={cancelBtnRef}
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 text-white"
            style={{
              backgroundColor: '#dc2626',
              fontFamily: 'Montserrat, sans-serif',
              minHeight: '48px',
              fontWeight: 600,
            }}
            aria-label={cancelButtonText}
          >
            {cancelButtonText}
          </Button>
          <Button
            onClick={onProceed}
            disabled={isProcessing || (recipientIdentityPrompt && !recipientIdValid)}
            className="flex-1 text-white"
            style={{
              backgroundColor: '#86BE41',
              fontFamily: 'Montserrat, sans-serif',
              minHeight: '48px',
              fontWeight: 600,
            }}
            aria-label={proceedButtonText}
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              proceedButtonText
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
